<template>
  <div class="currency-amount-input">
    <!-- Label -->
    <label v-if="label" class="currency-amount-input__label">{{ label }}</label>

    <!-- Input container -->
    <div :class="containerClasses">
      <!-- Content (input + help icon) -->
      <div class="currency-amount-input__content">
        <input
          ref="inputRef"
          v-model="localValue"
          type="text"
          :placeholder="placeholder"
          :disabled="locked"
          :aria-label="label"
          :aria-invalid="errorMessage ? 'true' : 'false'"
          :aria-describedby="errorMessage ? `${inputId}-error` : undefined"
          class="currency-amount-input__input"
          @input="handleInput"
          @focus="handleFocus"
          @blur="handleBlur"
        />

        <!-- Help icon with tooltip -->
        <div v-if="showHelpIcon" class="currency-amount-input__help-icon">
          <EXTooltip
            :title="helpTitle"
            :description="helpDescription"
            placement="top"
            :children="true"
          >
            <template #icon>
              <img
                src="@components/assets/help-circle.svg"
                alt="Help"
                class="currency-amount-input__help-icon-img"
              />
            </template>
          </EXTooltip>
        </div>
      </div>

      <!-- Suffix container (currency/period + chevron/lock icon) -->
      <div class="currency-amount-input__suffix">
        <span v-if="suffix" class="currency-amount-input__suffix-text">{{ suffix }}</span>
        <v-icon v-if="locked" size="16" class="currency-amount-input__lock-icon">
          mdi-lock
        </v-icon>
        <v-icon v-else size="16" class="currency-amount-input__chevron-icon">
          mdi-chevron-down
        </v-icon>
      </div>
    </div>

    <!-- Error message -->
    <div
      v-if="errorMessage"
      :id="`${inputId}-error`"
      class="currency-amount-input__error"
      role="alert"
    >
      {{ errorMessage }}
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import EXTooltip from '@components/tooltip/EXTooltip.vue';

/**
 * CurrencyAmountInput — numeric amount input with label and currency/period suffix.
 *
 * Used for money amounts in Fair Pay forms. Supports Empty, Focused, Filled, Error and Locked states.
 * Displays a label above, numeric input in the center, and a suffix (e.g. "USD/Year") on the right.
 *
 * @component
 */

const props = defineProps({
  /** Field label (e.g. "Base Salary*") */
  label: {
    type: String,
    default: ''
  },

  /** Amount value (v-model) */
  modelValue: {
    type: [Number, String],
    default: ''
  },

  /** Placeholder text shown in Empty state */
  placeholder: {
    type: String,
    default: '0.00'
  },

  /** Currency/period text (e.g. "USD/Year") */
  suffix: {
    type: String,
    default: ''
  },

  /** Error message; setting it puts the field in Error state */
  errorMessage: {
    type: String,
    default: ''
  },

  /** Read-only state with lock icon and disabled fill */
  locked: {
    type: Boolean,
    default: false
  },

  /** Shows help icon with tooltip */
  showHelpIcon: {
    type: Boolean,
    default: false
  },

  /** Help tooltip title */
  helpTitle: {
    type: String,
    default: ''
  },

  /** Help tooltip description */
  helpDescription: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['update:modelValue']);

// Local state
const localValue = ref(props.modelValue);
const isFocused = ref(false);
const inputRef = ref(null);

// Generate unique ID for accessibility
const inputId = computed(() => `currency-amount-input-${Math.random().toString(36).substr(2, 9)}`);

// Computed class binding for container
const containerClasses = computed(() => [
  'currency-amount-input__container',
  {
    'currency-amount-input__container--focused': isFocused.value,
    'currency-amount-input__container--error': !!props.errorMessage,
    'currency-amount-input__container--locked': props.locked
  }
]);

// Watch for external changes to modelValue
watch(() => props.modelValue, (newValue) => {
  localValue.value = newValue;
});

// Handlers
const handleInput = () => {
  // Format as number (allow decimals and commas)
  const formatted = localValue.value;
  emit('update:modelValue', formatted);
};

const handleFocus = () => {
  isFocused.value = true;
};

const handleBlur = () => {
  isFocused.value = false;
};
</script>

<style scoped>
.currency-amount-input {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 312px;
}

/* Label */
.currency-amount-input__label {
  font-family: Inter, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #344054;
  margin-bottom: 0;
}

/* Container (input + suffix) */
.currency-amount-input__container {
  display: flex;
  flex-direction: row;
  align-items: stretch;
  width: 312px;
  height: 44px;
  background-color: #ffffff;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  overflow: hidden;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

/* Focused state - brand border with focus ring */
.currency-amount-input__container--focused {
  border-color: #d6bbfb;
  box-shadow: 0 0 0 3px rgba(214, 187, 251, 0.3);
}

/* Error state */
.currency-amount-input__container--error {
  border-color: #fda29b;
}

.currency-amount-input__container--error.currency-amount-input__container--focused {
  box-shadow: 0 0 0 3px rgba(253, 162, 155, 0.3);
}

/* Locked state */
.currency-amount-input__container--locked {
  background-color: #e0e0e0;
  border-color: #d0d5dd;
}

/* Content area (input + help icon) */
.currency-amount-input__content {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  flex: 1;
  padding: 10px 0 10px 14px;
  min-width: 0;
}

/* Input field */
.currency-amount-input__input {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-family: Inter, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  color: #344054;
}

.currency-amount-input__input::placeholder {
  color: #475467;
  font-weight: 400;
}

.currency-amount-input__input:disabled {
  color: #4d4d4d;
  cursor: not-allowed;
}

/* Help icon */
.currency-amount-input__help-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.currency-amount-input__help-icon-img {
  width: 16px;
  height: 16px;
  display: block;
  cursor: pointer;
}

/* Suffix container */
.currency-amount-input__suffix {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  padding: 10px 14px;
  flex-shrink: 0;
}

.currency-amount-input__suffix-text {
  font-family: Inter, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #475467;
  white-space: nowrap;
}

.currency-amount-input__chevron-icon {
  color: #667085;
}

.currency-amount-input__lock-icon {
  color: #667085;
}

/* Error message */
.currency-amount-input__error {
  font-family: Inter, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #d92d20;
  margin-top: 4px;
}
</style>
